import React, { useState, useCallback, useEffect } from 'react';
import { useWallet } from '../contexts/WalletContext';
import { useNetwork } from '../contexts/NetworkContext';
import {
  getEscrowState,
  depositTokenA,
  depositTokenB,
  claimTokenA,
  claimTokenB,
  refundTokenA,
  refundTokenB,
  type EscrowState,
} from '../contracts/fixedTermsTokenEscrow';
import { ethers } from 'ethers';
import { getTokenSymbol, getTokenDecimals } from '../contracts/contract';
import { trustlineService } from '../services/trustline';
import './InteractEscrowPanel.css';

const ERC20_APPROVE_ABI = [
  'function approve(address spender, uint256 amount) returns (bool)',
  'function allowance(address owner, address spender) view returns (uint256)',
];

async function ensureAllowance(
  signer: ethers.Signer,
  account: string,
  tokenAddress: string,
  spenderAddress: string,
  requiredAmount: string
): Promise<void> {
  const tokenContract = new ethers.Contract(tokenAddress, ERC20_APPROVE_ABI, signer);
  let allowance = await tokenContract.allowance(account, spenderAddress);

  if (BigInt(allowance.toString()) < BigInt(requiredAmount)) {
    const approveTx = await tokenContract.approve(spenderAddress, requiredAmount);
    const receipt = await approveTx.wait();

    if (receipt && receipt.status === 1) {
      let retries = 5;
      while (retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        allowance = await tokenContract.allowance(account, spenderAddress);
        if (BigInt(allowance.toString()) >= BigInt(requiredAmount)) {
          return;
        }
        retries--;
      }
      if (BigInt(allowance.toString()) < BigInt(requiredAmount)) {
        throw new Error('Approval transaction confirmed but allowance not updated. Please try again.');
      }
    } else {
      throw new Error('Approval transaction failed.');
    }
  }
}

const InteractEscrowPanel: React.FC = () => {
  const { provider, signer, account } = useWallet();
  const { currentNetwork } = useNetwork();
  const [escrowAddress, setEscrowAddress] = useState('');
  const [escrowState, setEscrowState] = useState<EscrowState | null>(null);
  const [loading, setLoading] = useState(false);
  const [escrowLoading, setEscrowLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [escrowError, setEscrowError] = useState<string>('');
  const [tokenASymbol, setTokenASymbol] = useState<string>('');
  const [tokenBSymbol, setTokenBSymbol] = useState<string>('');
  const [tokenADecimals, setTokenADecimals] = useState<number>(18);
  const [tokenBDecimals, setTokenBDecimals] = useState<number>(18);

  const loadEscrowState = useCallback(async () => {
    if (!provider || !escrowAddress.trim()) return;
    setEscrowLoading(true);
    setEscrowError('');
    try {
      const state = await getEscrowState(provider, escrowAddress.trim());
      setEscrowState(state);
      const [symA, symB, decA, decB] = await Promise.all([
        getTokenSymbol(provider, state.tokenA),
        getTokenSymbol(provider, state.tokenB),
        getTokenDecimals(provider, state.tokenA),
        getTokenDecimals(provider, state.tokenB),
      ]);
      setTokenASymbol(symA);
      setTokenBSymbol(symB);
      setTokenADecimals(decA);
      setTokenBDecimals(decB);
    } catch (e: unknown) {
      setEscrowState(null);
      setEscrowError(e instanceof Error ? e.message : 'Failed to load escrow');
    } finally {
      setEscrowLoading(false);
    }
  }, [provider, escrowAddress]);

  useEffect(() => {
    if (!escrowAddress.trim()) {
      setEscrowState(null);
      setEscrowError('');
    }
  }, [escrowAddress]);

  const runAction = async (
    label: string,
    fn: () => Promise<{ wait: () => Promise<unknown> }>,
    preStep?: () => Promise<void>
  ) => {
    if (!signer || !escrowAddress.trim()) return;
    setLoading(true);
    setError('');
    try {
      if (preStep) await preStep();
      const tx = await fn();
      await tx.wait();
      await loadEscrowState();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : `${label} failed`);
    } finally {
      setLoading(false);
    }
  };

  const zeroAddress = ethers.ZeroAddress;
  const isDepositorOfA =
    account &&
    escrowState &&
    escrowState.depositorOfA !== zeroAddress &&
    account.toLowerCase() === escrowState.depositorOfA.toLowerCase();
  const isDepositorOfB =
    account &&
    escrowState &&
    escrowState.depositorOfB !== zeroAddress &&
    account.toLowerCase() === escrowState.depositorOfB.toLowerCase();
  const deadlinePassed = escrowState ? Number(escrowState.deadline) < Math.floor(Date.now() / 1000) : false;

  return (
    <div className="interact-escrow-panel card-base">
      <h3 className="interact-panel-header">Interact with a protected P2P trading escrow</h3>
      <div className="interact-form-group">
        <label>Escrow contract address</label>
        <input
          value={escrowAddress}
          onChange={(e) => setEscrowAddress(e.target.value)}
          placeholder="0x..."
        />
      </div>
      <button
        type="button"
        className="interact-panel-btn-load"
        onClick={loadEscrowState}
        disabled={escrowLoading || !escrowAddress.trim()}
      >
        {escrowLoading ? 'Loading...' : 'Load'}
      </button>

      {escrowError && <div className="interact-panel-error">{escrowError}</div>}

      {escrowState && (
        <div className="interact-escrow-state">
          <h4 className="interact-state-title">Escrow state</h4>

          <div className="interact-state-trade">
            <div className="interact-state-side interact-state-side-a">
              <div className="interact-state-side-label" title={escrowState.depositorOfA}>
                <span className="interact-state-role">Buyer</span>{' '}
                {escrowState.depositorOfA === zeroAddress ? (
                  <span className="interact-state-address-placeholder">Not yet deposited</span>
                ) : currentNetwork ? (
                  <a href={`${currentNetwork.blockExplorer}/address/${escrowState.depositorOfA}`} target="_blank" rel="noopener noreferrer" className="interact-panel-address-link interact-state-address">
                    {escrowState.depositorOfA.slice(0, 6)}…{escrowState.depositorOfA.slice(-4)} ↗
                  </a>
                ) : (
                  `${escrowState.depositorOfA.slice(0, 6)}…${escrowState.depositorOfA.slice(-4)}`
                )}
              </div>
              <div className="interact-state-token">
                {currentNetwork ? (
                  <a href={`${currentNetwork.blockExplorer}/address/${escrowState.tokenA}`} target="_blank" rel="noopener noreferrer" className="interact-panel-address-link">
                    {tokenASymbol} ↗
                  </a>
                ) : (
                  tokenASymbol
                )}
              </div>
              <div className="interact-state-amount">
                {ethers.formatUnits(escrowState.amountA, tokenADecimals)} {tokenASymbol}
              </div>
              <div className="interact-state-badges">
                <span className={`interact-state-badge ${escrowState.senderOfADeposited ? 'badge-done' : 'badge-pending'}`}>
                  {escrowState.senderOfADeposited ? 'Deposited' : 'Not deposited'}
                </span>
                {escrowState.isFunded && (
                  <span className={`interact-state-badge ${escrowState.senderOfAClaimed ? 'badge-done' : 'badge-pending'}`}>
                    {escrowState.senderOfAClaimed ? 'Claimed' : 'Not claimed'}
                  </span>
                )}
              </div>
            </div>
            <div className="interact-state-swap-icon" aria-hidden>⇄</div>
            <div className="interact-state-side interact-state-side-b">
              <div className="interact-state-side-label" title={escrowState.depositorOfB}>
                <span className="interact-state-role">Seller</span>{' '}
                {escrowState.depositorOfB === zeroAddress ? (
                  <span className="interact-state-address-placeholder">Not yet deposited</span>
                ) : currentNetwork ? (
                  <a href={`${currentNetwork.blockExplorer}/address/${escrowState.depositorOfB}`} target="_blank" rel="noopener noreferrer" className="interact-panel-address-link interact-state-address">
                    {escrowState.depositorOfB.slice(0, 6)}…{escrowState.depositorOfB.slice(-4)} ↗
                  </a>
                ) : (
                  `${escrowState.depositorOfB.slice(0, 6)}…${escrowState.depositorOfB.slice(-4)}`
                )}
              </div>
              <div className="interact-state-token">
                {currentNetwork ? (
                  <a href={`${currentNetwork.blockExplorer}/address/${escrowState.tokenB}`} target="_blank" rel="noopener noreferrer" className="interact-panel-address-link">
                    {tokenBSymbol} ↗
                  </a>
                ) : (
                  tokenBSymbol
                )}
              </div>
              <div className="interact-state-amount">
                {ethers.formatUnits(escrowState.amountB, tokenBDecimals)} {tokenBSymbol}
              </div>
              <div className="interact-state-badges">
                <span className={`interact-state-badge ${escrowState.senderOfBDeposited ? 'badge-done' : 'badge-pending'}`}>
                  {escrowState.senderOfBDeposited ? 'Deposited' : 'Not deposited'}
                </span>
                {escrowState.isFunded && (
                  <span className={`interact-state-badge ${escrowState.senderOfBClaimed ? 'badge-done' : 'badge-pending'}`}>
                    {escrowState.senderOfBClaimed ? 'Claimed' : 'Not claimed'}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="interact-state-meta">
            <div className="interact-state-meta-item">
              <span className="interact-state-meta-label">Deadline</span>
              <span className={`interact-state-meta-value ${deadlinePassed ? 'meta-passed' : ''}`}>
                {new Date(Number(escrowState.deadline) * 1000).toLocaleString()}
                {deadlinePassed && ' (passed)'}
              </span>
            </div>
            <div className="interact-state-meta-item">
              <span className="interact-state-meta-label">Status</span>
              <span
                className={`interact-state-badge ${
                  escrowState.isFunded && escrowState.senderOfAClaimed && escrowState.senderOfBClaimed
                    ? 'badge-done'
                    : deadlinePassed
                      ? 'badge-expired'
                      : escrowState.isFunded
                        ? 'badge-funded'
                        : 'badge-pending'
                }`}
              >
                {escrowState.isFunded && escrowState.senderOfAClaimed && escrowState.senderOfBClaimed
                  ? 'Settled'
                  : deadlinePassed
                    ? 'Expired'
                    : escrowState.isFunded
                      ? 'Funded'
                      : 'Not Funded'}
              </span>
            </div>
          </div>

          <div className="interact-escrow-actions">
            {!escrowState.senderOfADeposited && !deadlinePassed && (
              <button
                type="button"
                className="interact-panel-btn-primary"
                disabled={loading || !signer || !account}
                onClick={() =>
                  runAction(
                    `Deposit ${tokenASymbol}`,
                    () => depositTokenA(signer!, escrowAddress.trim()),
                    async () => {
                      await ensureAllowance(
                        signer!,
                        account!,
                        escrowState!.tokenA,
                        escrowAddress.trim(),
                        escrowState!.amountA.toString()
                      );
                      if (!currentNetwork) throw new Error('Network not available');
                      const valid = await trustlineService.validateEscrowDepositTokenA(
                        currentNetwork.chainId,
                        account!,
                        escrowAddress.trim()
                      );
                      if (!valid) throw new Error('Deposit failed: Trustline validation rejected');
                    }
                  )
                }
              >
                {loading ? '...' : `Deposit ${tokenASymbol}`}
              </button>
            )}
            {isDepositorOfA && escrowState.isFunded && !escrowState.senderOfAClaimed && (
              <button
                type="button"
                className="interact-panel-btn-primary"
                disabled={loading}
                onClick={() => runAction(`Claim ${tokenBSymbol}`, () => claimTokenB(signer!, escrowAddress.trim()))}
              >
                {loading ? '...' : `Claim ${tokenBSymbol}`}
              </button>
            )}
            {isDepositorOfA && deadlinePassed && !escrowState.isFunded && escrowState.senderOfADeposited && (
              <button
                type="button"
                className="interact-panel-btn-refund"
                disabled={loading}
                onClick={() => runAction(`Refund ${tokenASymbol}`, () => refundTokenA(signer!, escrowAddress.trim()))}
              >
                {loading ? '...' : `Refund ${tokenASymbol}`}
              </button>
            )}
            {!escrowState.senderOfBDeposited && !deadlinePassed && (
              <button
                type="button"
                className="interact-panel-btn-primary"
                disabled={loading || !signer || !account}
                onClick={() =>
                  runAction(
                    `Deposit ${tokenBSymbol}`,
                    () => depositTokenB(signer!, escrowAddress.trim()),
                    async () => {
                      await ensureAllowance(
                        signer!,
                        account!,
                        escrowState!.tokenB,
                        escrowAddress.trim(),
                        escrowState!.amountB.toString()
                      );
                      if (!currentNetwork) throw new Error('Network not available');
                      /*const valid = await trustlineService.validateEscrowDepositTokenB(
                        currentNetwork.chainId,
                        account!,
                        escrowAddress.trim()
                      );
                      if (!valid) throw new Error('Deposit failed: Trustline validation rejected');*/
                    }
                  )
                }
              >
                {loading ? '...' : `Deposit ${tokenBSymbol}`}
              </button>
            )}
            {isDepositorOfB && escrowState.isFunded && !escrowState.senderOfBClaimed && (
              <button
                type="button"
                className="interact-panel-btn-primary"
                disabled={loading}
                onClick={() => runAction(`Claim ${tokenASymbol}`, () => claimTokenA(signer!, escrowAddress.trim()))}
              >
                {loading ? '...' : `Claim ${tokenASymbol}`}
              </button>
            )}
            {isDepositorOfB && deadlinePassed && !escrowState.isFunded && escrowState.senderOfBDeposited && (
              <button
                type="button"
                className="interact-panel-btn-refund"
                disabled={loading}
                onClick={() => runAction(`Refund ${tokenBSymbol}`, () => refundTokenB(signer!, escrowAddress.trim()))}
              >
                {loading ? '...' : `Refund ${tokenBSymbol}`}
              </button>
            )}
            {account &&
              !isDepositorOfA &&
              !isDepositorOfB &&
              ((escrowState.senderOfADeposited && escrowState.senderOfBDeposited) || deadlinePassed) && (
                <p className="interact-panel-no-role">Your account is not a depositor for this escrow.</p>
              )}
          </div>
        </div>
      )}

      {error && <div className="interact-panel-error">{error}</div>}
    </div>
  );
};

export default InteractEscrowPanel;
